import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Link as MuiLink } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { bulkUploadTabs } from "pages/BulkUpload/contants";

import FGRefreshContext from "../FGRefreshContext";
import {
  SellerList,
  SoDashBoardFilter,
  SoTrackingTable,
  UploadDataModal,
} from "./components";

import { getCall, postCall_v2 } from "services";
import useDownload from "hooks/useDownload";
import { useToaster } from "hooks";
import { buildErrorMessage, formatDialogDate } from "utils";
import { success, error } from "constants";
import { normalizeSoBoardData } from "./makeData";
import {
  buildOrdersSearchPayload,
  buildSellerAnalyticsPayload,
  buildSellerFilters,
  getNextSellerSort,
  getSellerVmIds,
  normalizeSellerAnalyticsData,
  normalizeTabCounts
} from "./makeData";
import {
  SO_DASHBOARD_TABLE_FILTERS,
  DEFAULT_SELLER_SORT,
  EMPTY_SO_BOARD_DATA,
} from "./constants";
import {
  buildUploadFormData,
  buildUploadSuccessMessage,
  extractUploadRequestId,
  isEndpointConfigured,
  isFileSizeWithinLimit,
  isFileTypeAccepted,
} from "../SalesOrderRegister/makeData";
import {
  ACCEPTED_UPLOAD_FILE_TYPES,
  MAX_UPLOAD_SIZE_MB,
} from "../SalesOrderRegister/constants";

const SELLER_ANALYTICS_ENDPOINT =
  "/joms/api/internal/v1/so-listing/fetch-sellers";
const SO_BOARD_ENDPOINT = "/joms/api/internal/v1/so-listing/fetch-so-board";
const TAB_COUNTS_ENDPOINT = "/joms/api/v1/so-listing/tab-counts";

const SalesOrderRegister = () => {
  const { downloadDocumentByPost } = useDownload();
  const triggerToaster = useToaster();
  const navigate = useNavigate();

  const [sellerData, setSellerData] = useState([]);
  const [sellerPagination, setSellerPagination] = useState({});
  const [sellerList, setSellerList] = useState([]);
  const [selectedVmIds, setSelectedVmIds] = useState([]);
  const [filters, setFilters] = useState({});
  const [sellerSort, setSellerSort] = useState(DEFAULT_SELLER_SORT);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [customersData, setCustomersData] = useState([]);
  const [buyerLoading, setBuyerLoading] = useState(false);
  const [soData, setSoData] = useState(EMPTY_SO_BOARD_DATA);
  const [isLoading, setIsLoading] = useState(true);
  const [isSellerLoading, setIsSellerLoading] = useState(true);
  const [isSellerInitialized, setIsSellerInitialized] = useState(false);
  const [expandedRows, setExpandedRows] = useState([]);
  const [tableFilters, setTableFilters] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [tabCounts, setTabCounts] = useState({});

  const setSellerAnalyticsData = useCallback((response) => {
    const normalized = normalizeSellerAnalyticsData(response);
    setSellerData(normalized.sellers);
    setSellerPagination(normalized.pagination);
    setSelectedVmIds(getSellerVmIds(normalized.sellers));
  }, []);

  const fetchAllSellers = useCallback(async () => {
    try {
      const res = await getCall(
        "/joms/api/internal/v1/so-listing/fetch-filter-sellers",
      );
      const sellers = Array.isArray(res?.data) ? res.data : [];
      setSellerList(sellers);
    } catch {
      setSellerList([]);
    }
  }, []);

  const fetchSellerAnalytics = useCallback(
    async (
      activeFilters = {},
      activeSort = DEFAULT_SELLER_SORT,
      sellerPage = 0,
      sellerRowsPerPage = 10,
    ) => {
      let wasCancelled = false;
      setIsSellerLoading(true);
      try {
        const res = await postCall_v2(
          SELLER_ANALYTICS_ENDPOINT,
          buildSellerAnalyticsPayload(
            activeFilters,
            activeSort,
            sellerPage,
            sellerRowsPerPage,
          ),
        );

        if (!res) {
          wasCancelled = true;
          return;
        }

        if (res?.data?.success) {
          setSellerAnalyticsData(res?.data?.data);
        } else {
          setSellerAnalyticsData({ data: {} });
        }
      } catch {
        setSellerAnalyticsData({ data: {} });
      } finally {
        if (!wasCancelled) {
          setIsSellerLoading(false);
          setIsSellerInitialized(true);
        }
      }
    },
    [setSellerAnalyticsData],
  );

  const fetchCustomerData = useCallback(async (customerInput) => {
    if (!customerInput) {
      setCustomersData([]);
      return;
    }

    try {
      setBuyerLoading(true);
      setCustomersData([]);
      const res = await getCall(
        `/joms/api/external/v1/customer-details-by-reference/${customerInput}`,
      );
      setCustomersData(res?.data?.map((item) => item.companyName) ?? []);
    } catch {
      setCustomersData([]);
    } finally {
      setBuyerLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSellerAnalytics({}, DEFAULT_SELLER_SORT);
  }, [fetchSellerAnalytics]);

  useEffect(() => {
    void fetchAllSellers();
  }, [fetchAllSellers]);

  const handleApplyFilters = useCallback(
    (filterPayload = {}) => {
      if (!Object.keys(filterPayload).length) {
        setFilters({});
        setSelectedVmIds([]);
        setSoData(EMPTY_SO_BOARD_DATA);
        setExpandedRows([]);
        setPage(0);
        void fetchSellerAnalytics({}, sellerSort);
        return;
      }

      const nextFilters = buildSellerFilters(filterPayload, sellerList);

      setFilters(nextFilters);
      setSelectedVmIds([]);
      setExpandedRows([]);
      setPage(0);
      void fetchSellerAnalytics(nextFilters, sellerSort);
    },
    [fetchSellerAnalytics, sellerList, sellerSort],
  );

  const handleToggleSeller = useCallback((vmId) => {
    setPage(0);
    setExpandedRows([]);
    setSelectedVmIds((prev) =>
      prev.includes(vmId) ? prev.filter((id) => id !== vmId) : [...prev, vmId],
    );
  }, []);

  const handleToggleAllSellers = useCallback((vmIds) => {
    setPage(0);
    setExpandedRows([]);
    setSelectedVmIds(vmIds);
  }, []);

  const handleClearSelection = useCallback(() => {
    setFilters({});
    setSelectedVmIds([]);
    setSellerSort(DEFAULT_SELLER_SORT);
    setFilterResetKey((previousKey) => previousKey + 1);
    setCustomersData([]);
    setSoData(EMPTY_SO_BOARD_DATA);
    setExpandedRows([]);
    setPage(0);
    void fetchSellerAnalytics({}, DEFAULT_SELLER_SORT, 0, 10);
  }, [fetchSellerAnalytics]);

  const handleSellerSortChange = useCallback(
    (sortBy) => {
      const nextSort = getNextSellerSort(sellerSort, sortBy);
      setSellerSort(nextSort);
      void fetchSellerAnalytics(filters, nextSort);
    },
    [fetchSellerAnalytics, filters, sellerSort],
  );

  const handleSellerPageChange = useCallback(
    (event, nextPage) => {
      void fetchSellerAnalytics(
        filters,
        sellerSort,
        nextPage,
        Number(sellerPagination?.pageSize || 20),
      );
    },
    [fetchSellerAnalytics, filters, sellerPagination?.pageSize, sellerSort],
  );

  const handleSellerRowsPerPageChange = useCallback(
    (event) => {
      void fetchSellerAnalytics(filters, sellerSort, 0, Number(event.target.value));
    },
    [fetchSellerAnalytics, filters, sellerSort],
  );

  const fetchOrders = useCallback(async () => {
    if (!isSellerInitialized) {
      return;
    }

    let wasCancelled = false;
    setIsLoading(true);
    try {
      const payload = buildOrdersSearchPayload({
        page,
        rowsPerPage,
        filters,
        selectedVmIds,
        tableFilters,
      });

      const res = await postCall_v2(SO_BOARD_ENDPOINT, payload);

      if (!res) {
        wasCancelled = true;
        return;
      }

      if (res?.data) {
        setSoData(normalizeSoBoardData(res.data, tableFilters));
      } else {
        setSoData(EMPTY_SO_BOARD_DATA);
      }
    } catch {
      setSoData(EMPTY_SO_BOARD_DATA);
    } finally {
      if (!wasCancelled) {
        setIsLoading(false);
      }
    }
  }, [
    selectedVmIds,
    filters,
    page,
    rowsPerPage,
    tableFilters,
    isSellerInitialized,
  ]);

  useEffect(() => {
    void fetchOrders();
  }, [fetchOrders]);

  const pagination = useMemo(
    () => ({
      page,
      rowsPerPage,
      totalCount:
        soData.pagination?.totalRecords || soData.pagination?.totalItems || 0,
    }),
    [page, rowsPerPage, soData],
  );

  const handlePageChange = useCallback((_event, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleToggleExpand = useCallback((orderId) => {
    setExpandedRows((previousRows) =>
      previousRows.includes(orderId)
        ? previousRows.filter((id) => id !== orderId)
        : [...previousRows, orderId],
    );
  }, []);

  const handleToggleTableFilter = useCallback((key) => {
    setTableFilters((previousFilter) => (previousFilter === key ? "" : key));
    setExpandedRows([]);
    setPage(0);
  }, []);

  const exportPayload = useCallback(() => {
    return {
      soNumber: filters?.soNumber || null,
      orderNumber: filters?.slo || null,
      sourceSeller: selectedVmIds?.length ? selectedVmIds : null,
      filterType: tableFilters || null,
      soDateFrom: filters?.dateFrom || null,
      soDateTo: filters?.dateTo || null,
    };
  }, [tableFilters, filters, selectedVmIds]);

  const handleExport = useCallback(async () => {
    const payload = exportPayload();
    await downloadDocumentByPost(
      `/joms/api/internal/v1/so-listing/so-board-export`,
      payload,
      `sales-order-register-${formatDialogDate(Date.now())}.xlsx`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportPayload]);

  const fetchTabCounts = useCallback(async () => {
    try {
      const payload = buildOrdersSearchPayload({
        page,
        rowsPerPage,
        filters,
        selectedVmIds,
        tableFilters,
      });

      const res = await postCall_v2(TAB_COUNTS_ENDPOINT, payload);

      if (res?.data) {
        setTabCounts(normalizeTabCounts(res.data));
      } else {
        setTabCounts({});
      }
    } catch {
      setTabCounts({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedVmIds]);

  useEffect(() => {
    if (!isSellerInitialized) return;
    void fetchTabCounts();
  }, [fetchTabCounts, isSellerInitialized]);

  const refreshSoBoard = useCallback(() => {
    fetchOrders();
    fetchTabCounts();
  }, [fetchOrders, fetchTabCounts]);

  const handleUpload = useCallback(
    async (section, file) => {
      if (!isEndpointConfigured(section.uploadUrl)) {
        triggerToaster(
          `Upload is not available yet for ${section.title}.`,
          error,
        );
        return;
      }

      if (!isFileTypeAccepted(file, ACCEPTED_UPLOAD_FILE_TYPES)) {
        triggerToaster(
          `Unsupported file type. Accepted formats: ${ACCEPTED_UPLOAD_FILE_TYPES}.`,
          error,
        );
        return;
      }

      if (!isFileSizeWithinLimit(file, MAX_UPLOAD_SIZE_MB)) {
        triggerToaster(
          `File size should not exceed ${MAX_UPLOAD_SIZE_MB}MB.`,
          error,
        );
        return;
      }

      setUploadingKey(section.key);
      try {
        const res = await postCall_v2(
          section.uploadUrl,
          buildUploadFormData(file, section.actionType),
        );
        const responseData = res?.data;
        const requestId = extractUploadRequestId(responseData);
        const message = buildUploadSuccessMessage(responseData);
        const remarks = requestId ? responseData?.remarks : null;

        triggerToaster(
          requestId ? (
            <>
              {message}{" "}
              <MuiLink
                component="button"
                type="button"
                onClick={() =>
                  navigate(
                    `/bulk-upload?tab=${bulkUploadTabs.BULK_UPLOAD_STATUS_HISTORY}`,
                  )
                }
                sx={{ color: "inherit", fontWeight: 600 }}
              >
                Check the status of file
              </MuiLink>
              {remarks && (
                <>
                  <br />
                  Remarks:
                  <br />
                  {remarks}
                </>
              )}
            </>
          ) : (
            message
          ),
          success,
        );
      } catch (err) {
        triggerToaster(buildErrorMessage(err), error);
      } finally {
        setUploadingKey(null);
        handleCloseUploadModal();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [triggerToaster, navigate],
  );

  const handleOpenUploadModal = useCallback(() => setIsModalOpen(true), []);
  const handleCloseUploadModal = useCallback(() => setIsModalOpen(false), []);

  const getTableFilterCount = useCallback(
    (countKey) => Number(tabCounts?.[countKey] || 0),
    [tabCounts],
  );

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 0, alignItems: "stretch" }}>
        <Box sx={{ width: 290 }}>
          <SellerList
            sellerData={sellerData}
            selectedVmIds={selectedVmIds}
            onToggleSeller={handleToggleSeller}
            onToggleAllSellers={handleToggleAllSellers}
            onClearSelection={handleClearSelection}
            onSortChange={handleSellerSortChange}
            onApplyFilters={handleApplyFilters}
            sellerFilterData={sellerList}
            customersData={customersData}
            fetchCustomersData={fetchCustomerData}
            buyerLoading={buyerLoading}
            sellerPagination={sellerPagination}
            onPageChange={handleSellerPageChange}
            onRowsPerPageChange={handleSellerRowsPerPageChange}
            filterResetKey={filterResetKey}
            loading={isSellerLoading}
            sortConfig={sellerSort}
          />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <SoDashBoardFilter
            filters={SO_DASHBOARD_TABLE_FILTERS}
            selectedFilters={tableFilters}
            onToggleFilter={handleToggleTableFilter}
            getCount={getTableFilterCount}
            onExport={handleExport}
            onUpload={handleOpenUploadModal}
          />

          <Box m={3}>
            <FGRefreshContext.Provider value={refreshSoBoard}>
              <SoTrackingTable
                isLoading={isLoading}
                orders={soData.orders || []}
                pagination={pagination}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                expandedRows={expandedRows}
                onToggleExpand={handleToggleExpand}
                selectedFilters={tableFilters}
                filters={SO_DASHBOARD_TABLE_FILTERS}
              />
            </FGRefreshContext.Provider>
          </Box>
        </Box>
      </Box>

      <UploadDataModal
        open={isModalOpen}
        onClose={handleCloseUploadModal}
        uploadingKey={uploadingKey}
        onUpload={handleUpload}
      />
    </Box>
  );
};

export default SalesOrderRegister;
